import Service, { service } from '@ember/service';
import config from 'garaje/config/environment';
import type { PersonSuggestion } from 'garaje/models/badge-log/person-suggestion';
import type AjaxFetchService from 'garaje/services/ajax-fetch';

interface PersonSuggestionApiItem {
  name: string | null;
  email: string;
  eventCount: number;
}

interface PersonsResponse {
  persons: PersonSuggestionApiItem[];
}

/**
 * Service wrapping the badge-log JSON HTTP API. Endpoints here are NOT
 * ember-data resources — they return small, ephemeral payloads (e.g. the
 * person-search autocomplete) consumed directly by UI components.
 */
export default class BadgeLogService extends Service {
  @service declare ajax: AjaxFetchService;

  /**
   * Search for people who have triggered access events at the given
   * locations, matching a freetext query. Backs the access-events page
   * search autocomplete.
   *
   * Pass the same filters that scope the access-events table so the
   * dropdown stays in sync with what's visible: date range, activity
   * type, integration names, outcomes.
   *
   * @param query freetext search string (caller is responsible for
   *   debouncing and enforcing a minimum length)
   * @param locationIds list of location ids to scope the search to
   * @param options.startTime/endTime ISO-8601 date range
   * @param options.activityTypes list of activity types (e.g. ["ERRORS"])
   * @param options.integrationNames list of integration names
   * @param options.outcomes list of outcomes (e.g. ["SIGNED_IN_SUCCESSFULLY"])
   * @param options.limit max number of suggestions to return (defaults to 10)
   */
  async searchPersons(
    query: string,
    locationIds: string[],
    options: {
      startTime?: string;
      endTime?: string;
      activityTypes?: string[];
      integrationNames?: string[];
      outcomes?: string[];
      limit?: number;
    } = {},
  ): Promise<PersonSuggestion[]> {
    const params = new URLSearchParams({
      locationIds: locationIds.join(','),
      query,
      limit: String(options.limit ?? 10),
    });
    if (options.startTime) params.append('startTime', options.startTime);
    if (options.endTime) params.append('endTime', options.endTime);
    if (options.activityTypes?.length) params.append('activityTypes', options.activityTypes.join(','));
    if (options.integrationNames?.length) params.append('integrationNames', options.integrationNames.join(','));
    if (options.outcomes?.length) params.append('outcomes', options.outcomes.join(','));

    const url = `${config.badgeLogApiHost}/a/badge-log/v1/access-events/persons?${params.toString()}`;
    const response = await this.ajax.request<PersonsResponse>(url);

    return (response.persons ?? []).map((p) => ({
      name: p.name ?? null,
      email: p.email,
      eventCount: p.eventCount,
    }));
  }
}

declare module '@ember/service' {
  interface Registry {
    'badge-log': BadgeLogService;
  }
}
